"use client";

import { displayLabel } from "@/lib/display";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import {
  Activity,
  BookOpen,
  Gauge,
  History,
  MessageSquareText,
  Volume2,
  AudioLines,
  Mic,
  GraduationCap,
  Bot,
  Key,
  KeyRound,
  LayoutGrid,
  Link as LinkIcon,
  ListChecks,
  LogOut,
  MoreVertical,
  Shield,
  Users,
  Phone as PhoneIcon,
  Sun,
  Moon,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { useAuth } from "@/components/auth/auth-provider";
import { ChangePasswordDialog } from "@/components/auth/change-password-dialog";
import { logout } from "@/lib/auth";
import { cn } from "@/lib/utils";

interface NavItem {
  label: string;
  href: string;
  icon: typeof BookOpen;
  exact?: boolean;
  platformAdminOnly?: boolean;
  ownerOnly?: boolean;
  hiddenForPlatformAdmin?: boolean;
}

interface NavSection {
  group: string;
  amber?: boolean;
  hidden?: boolean;
  items: NavItem[];
  hiddenForPlatformAdmin?: boolean;
}

const OUTBOUND_NAV: NavSection[] = [
  {
    group: "数据概览",
    hiddenForPlatformAdmin: true,
    items: [
      { label: "概览", href: "/dashboard", icon: Activity, exact: true },
    ],
  },
  {
    group: "资源管理",
    hiddenForPlatformAdmin: true,
    items: [
      { label: "客服角色话本", href: "/playbooks", icon: BookOpen, exact: true },
      { label: "客服角色声音", href: "/voices", icon: Volume2, exact: true },
      { label: "客服AI角色", href: "/agent-backends", icon: Bot, exact: true },
    ],
  },
  {
    group: "客服会话",
    hiddenForPlatformAdmin: true,
    items: [
      { label: "文字客服", href: "/sandbox/text-chat", icon: MessageSquareText },
      { label: "语音应答", href: "/sandbox/text-audio", icon: AudioLines },
      { label: "语音对话客服", href: "/sandbox/voice-call", icon: Mic },
    ],
  },
  {
    group: "历史记录",
    hiddenForPlatformAdmin: true,
    items: [
      { label: "会话记录", href: "/history", icon: History, exact: true },
    ],
  },
  {
    group: "实时外呼",
    hidden: true, // Temporarily hide entry points while keeping routes available.
    amber: true,
    hiddenForPlatformAdmin: true,
    items: [
      { label: "外呼", href: "/live/outbound", icon: PhoneIcon },
      { label: "批次", href: "/live/batch", icon: ListChecks },
    ],
  },
  {
    group: "系统管理",
    items: [
      { label: "数据概览", href: "/admin/dashboard", icon: Activity, platformAdminOnly: true },
      { label: "运行控制台", href: "/admin/console", icon: Gauge, platformAdminOnly: true },
      { label: "外呼", href: "/admin/outbound", icon: PhoneIcon, platformAdminOnly: true },
      { label: "会话查询", href: "/admin/sessions", icon: History, platformAdminOnly: true },
      { label: "子账号", href: "/admin/users", icon: Users, ownerOnly: true },
      { label: "线路", href: "/admin/gateways", icon: LinkIcon },
      { label: "API 密钥", href: "/admin/api-keys", icon: Key },
      { label: "平台管理", href: "/admin/platform", icon: Shield, platformAdminOnly: true },
    ],
  },
];

const TRAINING_NAV: NavSection[] = [
  {
    group: "资源管理",
    items: [
      { label: "培训话术", href: "/training/playbooks", icon: BookOpen, exact: true },
      { label: "陪练角色", href: "/training/personas", icon: Users, exact: true },
    ],
  },
  {
    group: "训练",
    items: [
      { label: "训练活动", href: "/training/campaigns", icon: Target, exact: true },
      { label: "练习", href: "/training/practice", icon: GraduationCap, exact: true },
      { label: "历史记录", href: "/training/history", icon: History, exact: true },
    ],
  },
];

export function Sidemenu() {
  const pathname = usePathname();
  const router = useRouter();
  const { theme, setTheme } = useTheme();
  const { user, refresh } = useAuth();

  // Login page renders its own full-screen layout — hide sidebar there.
  if (pathname === "/login") return null;

  const isAdmin = !!user?.is_platform_admin;
  // Platform admin stays in outbound layout; training is a business domain.
  const mode = !isAdmin && pathname.startsWith("/training") ? "training" : "outbound";
  const rawNav = mode === "training" ? TRAINING_NAV : OUTBOUND_NAV;
  const nav = rawNav.filter(
    (section) => !section.hidden && !(isAdmin && section.hiddenForPlatformAdmin),
  );

  const handleLogout = async () => {
    await logout();
    await refresh();
    router.push("/login");
  };

  // is_anonymous is intentionally scoped to this component only — see CurrentUser docstring.
  const showAccountBlock = user && !user.is_anonymous;

  function isActive(href: string, exact?: boolean) {
    if (exact) return pathname === href;
    return pathname.startsWith(href);
  }

  return (
    <nav className="flex h-dvh w-56 flex-col overflow-hidden border-r">
      {/* App identity */}
      <div className="flex items-center gap-2 px-5 py-4">
        <PhoneIcon className="h-5 w-5" />
        <span className="text-sm font-semibold tracking-tight">Nord</span>
        {!isAdmin && (
          <Select
            value={mode}
            onValueChange={(v) =>
              router.push(v === "training" ? "/training/practice" : "/dashboard")
            }
          >
            <SelectTrigger
              size="sm"
              className="ml-auto h-7 w-7 justify-center border-0 bg-transparent p-0 shadow-none hover:bg-accent [&>svg:last-child]:hidden"
              aria-label="切换应用"
            >
              <LayoutGrid className="h-4 w-4" />
            </SelectTrigger>
            <SelectContent position="popper" align="end" sideOffset={4}>
              <SelectItem value="outbound">外呼</SelectItem>
              <SelectItem value="training">训练</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-7 w-7", isAdmin && "ml-auto")}
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          aria-label="切换主题"
        >
          <Sun className="h-4 w-4 dark:hidden" />
          <Moon className="hidden h-4 w-4 dark:block" />
        </Button>
      </div>

      {/* Navigation */}
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-3 py-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {nav.map((section) => (
          <div key={section.group} className="space-y-1">
            <p
              className={`mb-2 px-2 text-xs font-semibold tracking-wider uppercase ${
                section.amber
                  ? "text-amber-600 dark:text-amber-500"
                  : "text-muted-foreground"
              }`}
            >
              {section.group}
            </p>
            {section.items
              .filter((item) => {
                if (item.hiddenForPlatformAdmin && isAdmin) return false;
                if (item.platformAdminOnly) return isAdmin;
                if (item.ownerOnly) return user?.role === "owner" || isAdmin;
                return true;
              })
              .map((item) => {
              const active = isActive(item.href, item.exact);
              return (
                <Button
                  key={item.href}
                  asChild
                  variant="ghost"
                  className={`w-full justify-start gap-2 ${
                    active
                      ? "border-l-2 border-foreground bg-accent"
                      : "border-l-2 border-transparent"
                  }`}
                  size="sm"
                >
                  <Link href={item.href}>
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                </Button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Account block — hidden for anonymous sessions (didi_owner injection). */}
      {showAccountBlock && user && (
        <div className="border-t p-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold uppercase text-primary">
              {user.username.charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-sm font-medium" title={user.username}>
                  {user.username}
                </span>
                <Badge variant="secondary" className="shrink-0 text-[10px] uppercase">
                  {displayLabel(user.role)}
                </Badge>
              </div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span
                  className="truncate text-[11px] text-muted-foreground"
                  title={user.tenant_id}
                >
                  @{user.tenant_name || user.tenant_id}
                </span>
                {user.is_platform_admin && (
                  <Badge
                    variant="destructive"
                    className="shrink-0 px-1 py-0.5 text-[9px] leading-none"
                  >
                    平台管理员
                  </Badge>
                )}
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  aria-label="账户菜单"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-44">
                <ChangePasswordDialog>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <KeyRound className="mr-2 h-4 w-4" />
                    修改密码
                  </DropdownMenuItem>
                </ChangePasswordDialog>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={handleLogout}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  退出登录
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}
    </nav>
  );
}
